// Landing page "looks". "tech" is the live one; the others stay reachable with
// ?look=bold|quiet|editorial for comparison. Copy that changes per look
// lives here; the CSS overrides are in Looks.css, keyed on [data-look].
export type Look = 'bold' | 'quiet' | 'editorial' | 'tech';

export const LOOKS: { id: Look; label: string }[] = [
  { id: 'bold', label: 'Current (cleaned)' },
  { id: 'quiet', label: 'A · Quiet' },
  { id: 'editorial', label: 'B · Editorial' },
  { id: 'tech', label: 'C · Techie' },
];

export function readLook(): Look {
  if (typeof window === 'undefined') return 'tech';
  const q = new URLSearchParams(window.location.search).get('look');
  return q === 'bold' || q === 'quiet' || q === 'editorial' ? q : 'tech';
}

export interface LookCopy {
  heroTitle: [string, string];
  heroSub: string;
  introTitle: [string, string];
  introLede: string;
  devicesTitle: [string, string];
  devicesLede: string;
  galleryTitle: [string, string];
  finalTitle: [string, string];
  finalBtn: string;
  features: Record<string, { title: [string, string]; copy: string }>;
  importTitle?: string;
  importCopy?: string;
}

const BOLD: LookCopy = {
  heroTitle: ['Build worlds.', 'Meet inside them.'],
  heroSub: 'The 3D world builder that lives in your browser. Create with AI, show up as yourself, and hang out together in VR, on desktop, or on your phone.',
  introTitle: ['Everything a world needs.', 'In the browser.'],
  introLede: 'Create it with AI, build it by hand, show up as yourself, and bring everyone along on a headset, a laptop or a phone.',
  devicesTitle: ['One link.', 'Every device.'],
  devicesLede: 'No app store, no installs, no waiting. Open the link in a headset, a laptop or a phone and you’re in the same world as everyone else.',
  galleryTitle: ['Worlds people', 'are building now.'],
  finalTitle: ['Your world is', 'one link away.'],
  finalBtn: 'Start building free',
  features: {
    studio: { title: ['Describe a world.', 'Watch it get built.'], copy: 'Type what you want: a jazz club, a rooftop garden, a neon arcade. Studio sketches it, builds it in 3D, checks its own work against the sketch, and hands you a space you can walk into.' },
    avatars: { title: ['Your photo.', 'Your avatar.'], copy: 'Upload a picture or pick a look, and get a fully rigged 3D you, hair and outfit included. It walks, runs, dances and waves the moment it lands in your wardrobe.' },
    builder: { title: ['A real 3D editor.', 'Zero downloads.'], copy: 'Drag in models from the library, paint the sky, light the room and place seats, screens and portals. When it feels like somewhere, share one link and bring everyone in.' },
    games: { title: ['Pull up a chair.', 'Deal everyone in.'], copy: 'Put a game table anywhere in your world. Spades, Tonk, dominoes, chess and checkers for the table; trivia night for up to 20, drawing in the air, and truth or dare for the late crowd.' },
  },
};

const QUIET: LookCopy = {
  heroTitle: ['Build a place.', 'Bring your people.'],
  heroSub: 'GathrXR is a 3D world builder that runs in your browser. Make a space, walk around it as your own avatar, and invite friends in from a headset, a laptop or a phone.',
  introTitle: ['What’s inside', ''],
  introLede: 'Four tools, one account. Nothing to install.',
  devicesTitle: ['Works where you are', ''],
  devicesLede: 'Send someone the link. They open it on whatever they have and they’re in the same room as you.',
  galleryTitle: ['Recent worlds', ''],
  finalTitle: ['Make your first world tonight.', ''],
  finalBtn: 'Create a free account',
  features: {
    studio: { title: ['Studio', ''], copy: 'Describe a room (a jazz club, a rooftop garden, an arcade) and Studio builds it in 3D. You can start from a photo too, then move things around in the builder.' },
    avatars: { title: ['Avatars', ''], copy: 'Upload a photo or pick a starting look. You get a rigged 3D avatar that can walk, run, dance and wave, and you can keep several in your wardrobe.' },
    builder: { title: ['Builder', ''], copy: 'A full 3D editor in a browser tab. Pull models from the library, set the sky and lighting, add seats, screens and portals, then share the link.' },
    games: { title: ['Games', ''], copy: 'Drop a game table anywhere. Spades, Tonk, dominoes, chess and checkers, plus trivia for up to 20 people and truth or dare for later in the night.' },
  },
};

const EDITORIAL: LookCopy = {
  heroTitle: ['A world of your own,', 'open in a browser tab.'],
  heroSub: 'Build a place, show up as yourself, and hang out with the people you like. On a Quest, a laptop or your phone. No downloads.',
  introTitle: ['Here’s what you can do', 'once you’re in.'],
  introLede: 'Everything below is real and working today. The screenshots are from the live site.',
  devicesTitle: ['Same room,', 'different screens.'],
  devicesLede: 'Your cousin on a Quest, your friend on her phone at work, you on a laptop. Everyone’s in the same world.',
  galleryTitle: ['Worlds people', 'are building right now.'],
  finalTitle: ['Come build something.', ''],
  finalBtn: 'Start building, it’s free',
  features: {
    studio: { title: ['Describe a room.', 'Studio builds it.'], copy: 'Tell it “a jazz club with a low stage and red booths” or hand it a photo. A few minutes later you can walk around inside it, then rearrange anything you don’t like.' },
    avatars: { title: ['Show up', 'as yourself.'], copy: 'Make an avatar from a selfie or start from one of our looks. It comes rigged, so it walks, dances and waves right away.' },
    builder: { title: ['Build it', 'by hand.'], copy: 'The builder is a real 3D editor that runs in your browser. Models, skies, lighting, screens, portals. In VR you can grab things and resize them with your hands.' },
    games: { title: ['Then pull up', 'a chair.'], copy: 'Spades, Tonk, dominoes, chess, checkers, trivia for up to 20, and truth or dare when it gets late. Every game keeps a leaderboard.' },
  },
};

const TECH: LookCopy = {
  importTitle: 'Bring your own world.',
  importCopy: 'Built a scene in Blender or Unity? Export it as GLB or glTF and upload it as your world’s environment. Then light it, add seats, screens, portals and game tables on top, and share one link so people can walk in from a headset, a laptop or a phone.',
  heroTitle: ['Multiplayer 3D worlds', 'that run in a browser tab.'],
  heroSub: 'WebXR, WebGL and spatial voice. Build a world, drop in your avatar and share a URL. Works on Quest, desktop and mobile with nothing to install.',
  introTitle: ['Modules', ''],
  introLede: 'Four tools on one account. All of it ships today on gathrxr.com.',
  devicesTitle: ['Targets', ''],
  devicesLede: 'One URL, one codebase. Headsets get WebXR with hand tracking, desktops get keyboard and mouse, phones get touch.',
  galleryTitle: ['Recently updated worlds', ''],
  finalTitle: ['Spin up a world.', ''],
  finalBtn: 'Create a free account',
  features: {
    studio: { title: ['Prompt in, 3D room out.', ''], copy: 'Text or a reference image goes in. Studio drafts a layout, builds it in Blender on our GPUs, renders it back and compares against the draft before it hands you a GLB you can walk into.' },
    avatars: { title: ['Photo to rigged avatar.', ''], copy: 'A photo becomes a textured, skinned 3D model with a full humanoid rig, so walk, run, dance and emote animations work on it from the first load.' },
    builder: { title: ['A 3D editor, no install.', ''], copy: 'Transform gizmos, snapping, undo history, a model library, skyboxes, lights, screens and portals. In VR, grab and resize objects with your hands.' },
    games: { title: ['Realtime multiplayer games.', ''], copy: 'Spades, Tonk, dominoes, chess, checkers, trivia for up to 20 players, air drawing, and truth or dare. State syncs live and every game keeps a leaderboard.' },
  },
};

export const COPY: Record<Look, LookCopy> = { bold: BOLD, quiet: QUIET, editorial: EDITORIAL, tech: TECH };
